import * as React from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { resolveDriverRoute } from '../utils/resolveDriverRoute'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { FaIcon, faClose, faIdBadge, faIdBadgeSolid } from '@/lib/fa'
import {
  faFileContract,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import Tooltip from '../shared/ToolTip'
import {
  useQueryDriverRequiredActions,
  DriverRequiredActions,
  DriverRequiredActionItem,
} from '@/lib/api/company/drivers/useQueryDriverRequiredActions'
import { getOutOfDateCauseKey } from '../shared/timesheetOutOfDateCause'
import { getDriverActionRoute } from '../shared/driverActionRoute'
import { LoaderIcon } from '@/app/features/icons/Loader'
import clx from 'classnames'
import moment from 'moment'
import SearchColor from '../../../assets/icons_new/SearchColor.svg'
import SearchInput from '../shared/SearchInput'
import GroupPicker from '../shared/DriverGroupPicker'
import Users from '../../../assets/icons_new/Users.svg'
import { GroupModel } from '@/app/models/groupModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import Checkbox from '../shared/Checkbox'
import { CompanyNameBadge } from '../shared/viewBars/CompanyNameBadge'
import { useSideSelectionStore } from '../stores/sideSelectionStore'

type Props = {
  companyId: string
}

const SideDriversTable: React.FC<Props> = ({ companyId }) => {
  const {
    basicDrivers,
    search,
    setSearch,
    setSelectedDriverId,
    isLoadingBasicDrivers,
    isFetchingBasicDrivers,
    groups,
    setActualGroup,
    actualGroup,
    actualGroupDriverIds: groupDriverIds,
    actualGroupDriverIdsFor: groupDriverIdsFor,
    refresh,
  } = useDrivers()

  const { sideCheckedIds, sideIsActive, toggleSide, toggleAllSide, clearSide } =
    useSideSelectionStore()

  const { t, tSafe } = useLocalize()
  const dates = useDate()

  // Zadania przy kierowcach dla wybranego miesiaca. Osobne zapytanie od listy, zeby panel
  // pokazal sie od razu, a znaczniki dosypaly sie chwile pozniej.
  const { data: requiredActions = [] } = useQueryDriverRequiredActions({
    companyId,
    month: dates.month || undefined,
  })

  const actionsByDriverId = React.useMemo(() => {
    const map: Record<number, DriverRequiredActions> = {}
    for (const entry of requiredActions) {
      map[entry.driverId] = entry
    }
    return map
  }, [requiredActions])

  const describeAction = React.useCallback(
    (item: DriverRequiredActionItem) => {
      const text = tSafe(`driverActions.${item.type}`)
      const withCount = (
        typeof item.count === 'number'
          ? text.replace('{count}', String(item.count))
          : text
      ).replace('{date}', item.date ? moment(item.date).format('DD.MM.YYYY') : '')

      // Przy nieaktualnej ewidencji dopisujemy, co ja uniewaznilo - inaczej uzytkownik wie,
      // ze ma przeliczyc, ale nie wie, czy powodem byla delegacja, umowa czy ustawienia,
      // czyli nie wie, czy przed przeliczeniem zostalo mu jeszcze cos do sprawdzenia.
      const causeKey = getOutOfDateCauseKey(item.cause)
      return causeKey ? `${tSafe(causeKey)} ${withCount}` : withCount
    },
    [tSafe],
  )

  const [showSearch, setShowSearch] = React.useState(false)
  // A typed-in query keeps the bar visible, so the active filter is never hidden.
  const isSearchOpen = showSearch || search.trim().length > 0

  const handleSelectGroup = (option: GroupModel | null) => {
    if (!option || option.id === 0) {
      setActualGroup(null)
    } else if (option.id < 0) {
      setActualGroup(option)
    } else {
      const group = groups.find((g) => g.id === option.id)
      if (group) setActualGroup(group)
    }
  }

  const navigate = useNavigate()
  const location = useLocation()
  const { driverId: driverIdParam } = useParams<{ driverId?: string }>()
  const activeId = driverIdParam ? Number(driverIdParam) : 0

  // Znacznik prowadzi do PIERWSZEGO zadania z listy, czyli najwczesniejszego kroku
  // rozliczenia - serwer oddaje pozycje juz w tej kolejnosci.
  const goToDriverAction = React.useCallback(
    (driverId: number, list: 'items' | 'taxExemptItems' = 'items') => {
      const entry = actionsByDriverId[driverId]
      const item = entry?.[list]?.[0]
      if (!item || !dates.month) return

      const target = getDriverActionRoute(item, {
        companyId,
        driverId,
        month: dates.month,
      })
      if (!target) return

      setSelectedDriverId(driverId)
      navigate(target)
    },
    [actionsByDriverId, companyId, dates.month, navigate, setSelectedDriverId],
  )


  const handleSelectDriver = (driverId: number) => {
    setSelectedDriverId(driverId)

    const { pathname, search: qs, hash } = location

    if (/\/driver\/[^/]+/.test(pathname)) {
      const newPath = pathname.replace(/(\/driver\/)[^/]+/, `$1${driverId}`)
      navigate({ pathname: newPath, search: qs, hash })
      return
    }

    const dateRange = dates.getDateRangeForView()
    const config = resolveDriverRoute(pathname, {
      driverId: String(driverId),
      from: dateRange.from,
      to: dateRange.to,
      month: dates.month || '',
      year: dates.year || '',
    })

    if (config.routeSingle?.url) {
      navigate({ pathname: config.routeSingle.url, search: qs, hash })
      return
    }

    // fallback for routes without a paired single route (e.g. group contracts)
    const match = pathname.match(/^(.*\/driverModule)(\/.*)?$/)
    if (match) {
      navigate({
        pathname: `${match[1]}${match[2] ?? ''}/driver/${driverId}`,
        search: qs,
        hash,
      })
    }
  }

  const activeDrivers = React.useMemo(() => {
    if (!actualGroup || actualGroup.id === 0) return basicDrivers
    if (actualGroup.id === -1) return basicDrivers.filter((d) => d.isActive)
    if (actualGroup.id === -2) return basicDrivers.filter((d) => !d.isActive)
    return basicDrivers.filter(
      (d) => groupDriverIds.includes(d.id) && d.isActive,
    )
  }, [basicDrivers, actualGroup, groupDriverIds])

  // Akcje (przeliczenie, wydruk, usuwanie) biora zaznaczenie ze store'u, wiec kierowcy
  // ukryci filtrem grupy nie moga w nim zostac. WPF publikuje tylko zaznaczonych z biezacej
  // grupy (ActiveDriversSelected); wyszukiwarka zaznaczenia nie zaweza.
  const groupId = actualGroup?.id ?? 0
  const groupKnown =
    !isLoadingBasicDrivers &&
    basicDrivers.length > 0 &&
    (groupId <= 0 || groupDriverIdsFor === groupId)

  React.useEffect(() => {
    if (!groupKnown) return
    const inGroup = new Set(activeDrivers.map((d) => d.id))
    const kept = sideCheckedIds.filter((id) => inGroup.has(id))
    if (kept.length === sideCheckedIds.length) return
    if (kept.length === 0) clearSide()
    else toggleAllSide(kept, true, sideIsActive)
  }, [
    groupKnown,
    activeDrivers,
    sideCheckedIds,
    sideIsActive,
    toggleAllSide,
    clearSide,
  ])

  const filteredDrivers = React.useMemo(() => {
    if (!search || !search.trim()) return activeDrivers
    const q = search.toLowerCase()
    return activeDrivers.filter((d) =>
      (d.fullName ?? '').toLowerCase().includes(q),
    )
  }, [activeDrivers, search])

  const { sortedData } = useSortableData({
    data: filteredDrivers,
    tableColumns: [],
  })

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text
    // Wyszukiwanie filtruje po zwyklym podciagu, wiec znaki regexa trzeba escapowac.
    // split z grupa przechwytujaca daje trafienia na nieparzystych indeksach.
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const parts = text.split(new RegExp(`(${escaped})`, 'i'))
    return (
      <>
        {parts.map((part, i) =>
          i % 2 === 1 ? (
            <span key={i} className="tw-bg-[#FFF47B]">
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </>
    )
  }

  return (
    <div className="tw-flex tw-h-full tw-w-[300px] tw-max-w-[300px] tw-flex-col">
      <div className={`tw-p-2 tw-pt-3.5`}>
        <CompanyNameBadge fullWidth />
      </div>
      <div className="tw-z-[40] tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 tw-bg-white tw-pr-4 tw-shadow-sm">
        <div className="tw-flex tw-w-fit tw-items-center tw-gap-2 tw-p-3">
          <div
            className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-2 tw-p-1 ${isSearchOpen ? 'tw-rounded-lg tw-border-primary' : 'tw-border-transparent'}`}
            onClick={() => setShowSearch((prev) => !prev)}
          >
            <img src={SearchColor} alt="Search Icon" className="tw-w-6" />
          </div>
        </div>
        <div className="tw-z-[200] tw-w-full">
          <GroupPicker
            fullWidth
            companyId={companyId}
            refreshGroups={refresh}
            name={t('common.all')}
            options={groups.map((g) => ({ id: g.id, name: g.name }))}
            onSelect={handleSelectGroup}
            icon={Users}
            actualGroup={actualGroup}
          />
        </div>
      </div>

      {isSearchOpen && (
        <div className="tw-z-[70] tw-flex-shrink-0 tw-border-b tw-border-[#e5e6e6] tw-bg-white tw-px-1">
          <SearchInput
            inline
            search={search}
            searchResults={filteredDrivers.length}
            setSearch={setSearch}
            handleClickOutside={() => setShowSearch(false)}
          />
        </div>
      )}

      <div className="custom-scrollbar tw-relative tw-flex-1 tw-overflow-y-auto">
        {isFetchingBasicDrivers && !isLoadingBasicDrivers && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-start tw-justify-center tw-bg-white/60 tw-pt-6">
            <LoaderIcon className="tw-animate-spin tw-text-primary" />
          </div>
        )}
        {isLoadingBasicDrivers ? (
          <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        ) : sortedData.length === 0 ? (
          <div className="tw-p-4 tw-text-center tw-text-xs tw-text-content-base">
            Brak kierowców
          </div>
        ) : (
          <ul className="tw-flex tw-flex-col">
            <li className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-base-200 tw-bg-content-primary tw-px-3 tw-py-2">
              <Checkbox
                checked={
                  sortedData.length > 0 &&
                  sortedData.every((d) => sideCheckedIds.includes(d.id))
                }
                onChange={(checked) =>
                  toggleAllSide(
                    sortedData.map((d) => d.id),
                    checked,
                    sortedData[0]?.isActive,
                  )
                }
              />
              <span className="tw-text-xs tw-font-medium tw-text-content-base">
                {
                  sideCheckedIds.filter((id) =>
                    sortedData.some((d) => d.id === id),
                  ).length
                }{' '}
                / {sortedData.length}{' '}
              </span>

              <button
                type="button"
                disabled={sideCheckedIds.length === 0}
                onClick={clearSide}
                title="Wyczyść zaznaczonych kierowców"
                className={`tw-ml-auto tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1 tw-rounded-md tw-px-2 tw-py-0.5 tw-text-xs tw-text-content-base tw-transition-colors hover:tw-bg-base-200 ${sideCheckedIds.length === 0 ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer'}`}
              >
                <FaIcon icon={faClose} className="tw-h-3 tw-w-3" />
                <span>Wyczyść</span>
              </button>
            </li>
            {sortedData.map((driver) => (
              <li
                key={driver.id}
                onClick={() => handleSelectDriver(driver.id)}
                className={clx(
                  'tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-b tw-border-base-300/50 tw-px-3 tw-py-2 tw-text-sm tw-transition-colors hover:tw-bg-primary/10',
                  activeId === driver.id && 'tw-bg-primary/20 tw-font-medium',
                  !driver.isActive && 'tw-opacity-50',
                )}
              >
                <div
                  className="tw--m-2 tw-grid tw-place-items-center tw-p-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleSide(driver.id, driver.isActive)
                  }}
                >
                  <Checkbox
                    checked={sideCheckedIds.includes(driver.id)}
                    onChange={() => toggleSide(driver.id, driver.isActive)}
                  />
                </div>
                <FaIcon
                  icon={activeId === driver.id ? faIdBadgeSolid : faIdBadge}
                  className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-text-content-base"
                />
                <span className="tw-min-w-0 tw-truncate tw-text-content-base">
                  {highlightMatch(driver.fullName ?? '', search)}
                </span>
                {/*
                  Jeden znacznik, kolor = waga (blokuje / do zrobienia), a co dokladnie
                  zrobic mowi dymek. Kolor per rodzaj zadania bylby nie do zapamietania
                  przy szesciu rodzajach i nie do odczytania przy daltonizmie.
                */}
                {actionsByDriverId[driver.id]?.items?.length > 0 && (
                  <span
                    className="tw-ml-auto tw-flex tw-flex-shrink-0 tw-items-center"
                    onClick={(e) => {
                      // Nie wchodzimy w zwykla nawigacje wiersza (biezaca zakladka
                      // kierowcy), tylko idziemy tam, gdzie sie te zadanie wykonuje.
                      e.stopPropagation()
                      goToDriverAction(driver.id)
                    }}
                  >
                    <Tooltip
                      asChild
                      portal
                      maxWidth="20rem"
                      text={actionsByDriverId[driver.id].items
                        .map(describeAction)
                        .join(' ')}
                    >
                      <FaIcon
                        icon={faTriangleExclamation}
                        className={clx(
                          'tw-h-3.5 tw-w-3.5 tw-cursor-pointer',
                          actionsByDriverId[driver.id].severity === 'critical'
                            ? 'tw-text-red-500'
                            : actionsByDriverId[driver.id].severity === 'warning'
                              ? 'tw-text-amber-600'
                              : 'tw-text-content-muted',
                        )}
                      />
                    </Tooltip>
                  </span>
                )}
                {/* Ulga dla młodych - osobna ikonka, żeby nie przestawiała kroków rozliczenia. */}
                {actionsByDriverId[driver.id]?.taxExemptItems?.length > 0 && (
                  <span
                    className={clx(
                      'tw-flex tw-flex-shrink-0 tw-items-center',
                      actionsByDriverId[driver.id].items.length > 0
                        ? 'tw-ml-1.5'
                        : 'tw-ml-auto',
                    )}
                    onClick={(e) => {
                      e.stopPropagation()
                      goToDriverAction(driver.id, 'taxExemptItems')
                    }}
                  >
                    <Tooltip
                      asChild
                      portal
                      maxWidth="20rem"
                      text={actionsByDriverId[driver.id].taxExemptItems
                        .map(describeAction)
                        .join(' ')}
                    >
                      <FaIcon
                        icon={faFileContract}
                        className={clx(
                          'tw-h-3.5 tw-w-3.5 tw-cursor-pointer',
                          actionsByDriverId[driver.id].taxExemptItems.some(
                            (item) => item.severity === 'critical',
                          )
                            ? 'tw-text-red-500'
                            : 'tw-text-amber-600',
                        )}
                      />
                    </Tooltip>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default SideDriversTable
